import React, { useEffect, useRef, useState } from 'react';
import type Hls from 'hls.js';
import { buttonReset, color, font, radius, space, weight } from '../theme';
import Icon from './Icon';
import { ButtonLink } from './Button';

/* An HLS player for the Knesset's streams: live broadcasts (plenum, committees) and
   recordings (bill timeline, past sessions), which send Access-Control-Allow-Origin: *.
   Check a URL with utils/live canPlayInApp() first and link out when it's false: plenum
   recordings on video.knesset.gov.il (…mp4:vod/PLM/…) get a WAF challenge, not the playlist.
   - Native HLS where the browser has it (Safari, iOS, recent Chromium), else hls.js's light
     build, loaded on demand in its own chunk when the player mounts (PlayerDialog mounts it
     only when opened). If native playback fails and the browser has Media Source
     Extensions, hls.js takes over.
   - Click to play: nothing plays or downloads video until the viewer presses play (the
     overlay button or the native controls); hls.js reads only the playlist before that.
   - Media controls stay left-to-right, as media timelines do in RTL interfaces.
   - Unsupported browser or a stream that fails: a message and a link to the Knesset site. */

interface LivePlayerProps {
  src: string;                    // the .m3u8 playlist
  poster?: string | null;
  live?: boolean;                 // labels only: a broadcast rather than a recording
  title?: string;                 // the video's accessible name
  // Where to watch instead when the stream can't play here (the session's page)
  fallbackUrl?: string | null;
  fallbackLabel?: string;
  style?: React.CSSProperties;
}

type PlayerState = 'loading' | 'ready' | 'unsupported' | 'error';

const HLS_MIME = 'application/vnd.apple.mpegurl';

// Media Source Extensions, which hls.js needs (iPhones before iOS 17.1 have none)
const hasMediaSource = (): boolean => typeof window !== 'undefined' && ('MediaSource' in window || 'ManagedMediaSource' in window);

const box: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  aspectRatio: '16 / 9',
  borderRadius: radius.lg,
  overflow: 'hidden',
  background: 'radial-gradient(120% 90% at 50% 0%, #26364e 0%, #121b29 60%, #0b111b 100%)',
  color: color.textOnPrimary,
};

const LivePlayer = ({ src, poster, live = false, title, fallbackUrl, fallbackLabel = 'פתיחה באתר הכנסת', style }: LivePlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  // hls.js loads the playlist only; segments start on the first play
  const loadStartedRef = useRef(false);
  // Play as soon as the stream is attached (pressed play while hls.js was loading, or the
  // native player failed and hls.js takes over)
  const wantPlayRef = useRef(false);
  const startedRef = useRef(false);
  const srcRef = useRef<string | null>(null);
  const [state, setState] = useState<PlayerState>('loading');
  const [started, setStarted] = useState(false);
  // The src whose native playback failed: hls.js plays it instead (Chromium's newer native
  // HLS doesn't play every stream hls.js does)
  const [hlsJsFor, setHlsJsFor] = useState<string | null>(null);
  const forceHlsJs = hlsJsFor === src;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;
    let hls: Hls | null = null;
    let recovered = false;
    if (srcRef.current !== src) {
      // A new stream starts from the play button again
      srcRef.current = src;
      wantPlayRef.current = false;
      startedRef.current = false;
      setStarted(false);
    }
    loadStartedRef.current = false;
    setState('loading');

    const native = !forceHlsJs && video.canPlayType(HLS_MIME) !== '';
    const fail = () => { if (!cancelled) setState('error'); };
    const onVideoError = () => {
      if (cancelled) return;
      if (native && hasMediaSource()) {
        wantPlayRef.current = startedRef.current;
        setHlsJsFor(src);
        return;
      }
      fail();
    };
    video.addEventListener('error', onVideoError);

    if (native) {
      // Native HLS (preload="none": nothing loads before play)
      video.src = src;
      setState('ready');
      if (wantPlayRef.current) video.play().catch(() => { /* the viewer can press play again */ });
    } else {
      import('hls.js/light').then(({ default: HlsClass }) => {
        if (cancelled) return;
        if (!HlsClass.isSupported()) { setState('unsupported'); return; }
        hls = new HlsClass({ autoStartLoad: false });
        hlsRef.current = hls;
        hls.on(HlsClass.Events.ERROR, (_event, data) => {
          if (!data.fatal || !hls) return;
          // A media decode error is often recoverable once; anything else ends playback here
          if (data.type === HlsClass.ErrorTypes.MEDIA_ERROR && !recovered) {
            recovered = true;
            hls.recoverMediaError();
            return;
          }
          fail();
        });
        hls.loadSource(src);
        hls.attachMedia(video);
        setState('ready');
        if (wantPlayRef.current) {
          loadStartedRef.current = true;
          hls.startLoad();
          video.play().catch(() => { /* the viewer can press play again */ });
        }
      }).catch(fail);
    }

    return () => {
      cancelled = true;
      video.removeEventListener('error', onVideoError);
      if (hls) hls.destroy();
      hlsRef.current = null;
      // Stop a native stream from downloading on
      video.pause();
      video.removeAttribute('src');
      video.load();
    };
  }, [src, forceHlsJs]);

  // The first play, from the overlay or the native controls: start loading segments
  const onPlay = () => {
    startedRef.current = true;
    setStarted(true);
    const hls = hlsRef.current;
    if (hls && !loadStartedRef.current) {
      loadStartedRef.current = true;
      hls.startLoad();
    }
  };

  const playNow = () => {
    const video = videoRef.current;
    if (!video) return;
    startedRef.current = true;
    if (state === 'loading') { wantPlayRef.current = true; setStarted(true); return; }
    video.play().catch(() => { /* blocked or failed: the error event or the controls take over */ });
  };

  const unavailable = state === 'error' || state === 'unsupported';

  return (
    <div dir="ltr" style={{ ...box, ...style }}>
      <video
        ref={videoRef}
        controls={!unavailable && started}
        playsInline
        preload="none"
        poster={poster || undefined}
        aria-label={title || (live ? 'שידור חי' : 'הקלטה')}
        onPlay={onPlay}
        style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain', display: unavailable ? 'none' : 'block', background: 'transparent' }}
      />

      {/* Before the first play: one large play button over the poster */}
      {!unavailable && !started && (
        <button
          type="button"
          onClick={playNow}
          aria-label={live ? 'הפעלת השידור' : 'הפעלת ההקלטה'}
          style={{
            ...buttonReset,
            position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, width: '100%', height: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <span style={{
            width: 64, height: 64, borderRadius: '50%', background: 'rgba(16,24,40,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: color.textOnPrimary,
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          }}>
            <Icon name="play" size={30} />
          </span>
        </button>
      )}

      {unavailable && (
        <div dir="rtl" role="status" style={{
          position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: space.md, padding: space.lg, textAlign: 'center',
        }}>
          <Icon name="alert" size={28} />
          <span style={{ fontSize: font.label, fontWeight: weight.medium, lineHeight: 1.5 }}>
            {state === 'unsupported'
              ? 'הדפדפן הזה לא יכול לנגן את השידור כאן'
              : live ? 'השידור לא נטען' : 'ההקלטה לא נטענה'}
          </span>
          {fallbackUrl && <ButtonLink href={fallbackUrl} icon="external">{fallbackLabel}</ButtonLink>}
        </div>
      )}
    </div>
  );
};

export default LivePlayer;
